import { Grid, Stack, Typography, Autocomplete, TextField } from "@mui/material";
import CustomButton from "components/atoms/CustomButton";
import CustomTextInput from "components/atoms/CustomTextInput";
import ImageUpload from "components/atoms/ImageUpload";
// import CustomSelect from "components/atoms/Select"; // [NOTE] Removed as we are using Autocomplete
import { useFormik } from "formik";
import React, { useMemo } from "react"; // [CHANGED] Added useMemo
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { handleErrorProps } from "utils/handleErrorProps";
import {
  GET_ALL_PHARMACY_PRODUCTS,
  GET_DRUG_TYPES,
  GET_PHARMACY_GENERICS,
} from "utils/reactQueryKeys";

const defaultInitialValues = {
  drugName: "",
  drugType: "",
  unit: "",
  genericName: "",
  strength: "",
  brandName: "",
  prodcutDescription: "",
  filename: "",
  salesPrice: "",
  purchasePrice: "",
};

const requireFields = ["drugName"];

const validate = (values) => {
  const errors = {};
  requireFields.forEach((field) => {
    if (!values[field]) return (errors[field] = "Required");
  });
  return errors;
};

// [CHANGED] Helper to format data based on presence of file
const formatData = (data) => {
  if (!data.filename) {
    // If no file, return plain object (clean up filename key)
    const copy = { ...data };
    delete copy.filename;
    return copy;
  }
  // If file exists, return FormData
  const formData = new FormData();
  for (const key in data) {
    formData.append(key, data[key]);
  }
  return formData;
};

// [CHANGED] Props: isEditMode, initialData
function CreateNewProductModal({ closeModal, isEditMode = false, initialData = null }) {
  const queryClient = useQueryClient();

  // [NEW] Compute initial values to populate form if editing
  const formValues = useMemo(() => {
    if (isEditMode && initialData) {
      // [FIX] Helper to safely get ID whether populated (object) or unpopulated (string)
      const getId = (field) => {
        const val = initialData[field];
        if (!val) return "";
        if (typeof val === 'object') return val._id || val.id || ""; // Handle populated object
        return val; // Handle string ID
      };

      return {
        drugName: initialData.drugName || "",
        drugType: getId('drugType'), 
        unit: initialData.unit || "",
        genericName: getId('genericName'),
        strength: initialData.strength || "",
        brandName: initialData.brandName || "",
        prodcutDescription: initialData.prodcutDescription || "",
        filename: "", 
        salesPrice: initialData.salesPrice || "",
        purchasePrice: initialData.purchasePrice || "",
      };
    }
    return defaultInitialValues;
  }, [isEditMode, initialData]);

  // [HELPER] Safe access to initialData names
  const getInitialOption = (field, idValue) => {
    if (!initialData || !initialData[field]) return null;
    // If initialData has the full object and the ID matches the current form value
    const initialField = initialData[field];
    if (typeof initialField === 'object' && (initialField._id === idValue || initialField.id === idValue)) {
      return { name: initialField.name || initialField.activeIngredient, value: idValue };
    }
    return null;
  };

  const {
    handleChange,
    values,
    touched,
    errors,
    handleBlur,
    setFieldTouched,
    resetForm,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: formValues, // [CHANGED]
    enableReinitialize: true,  // [CHANGED] Important for updates
    validate: (values) => validate(values),
  });

  // Queries (Unchanged)
  const { data: drugTypes } = useCustomQuery(
    GET_DRUG_TYPES,
    { url: `/drugtype/getall`, method: "get", avoidCancelling: true },
    {
      refetchOnWindowFocus: false,
      select: (data) => data.data.map((drug) => ({ name: drug.name, value: drug._id })),
    }
  );

  const { data: genericNames } = useCustomQuery(
    GET_PHARMACY_GENERICS,
    { url: `/generic/getall`, method: "get", avoidCancelling: true },
    {
      refetchOnWindowFocus: false,
      select: (data) => data.data.map((generic) => ({ name: generic.activeIngredient, value: generic._id })),
    }
  );

  // [EXISTING] Create Mutation
  const { mutate: createProduct, isLoading: createProductLoading } =
    useCustomMutation(
      {
        url: `/product/create`,
        method: "post",
        data: formatData(values),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [GET_ALL_PHARMACY_PRODUCTS] });
          toast.success("Success");
          resetForm();
          closeModal();
        },
        onError: (error) => {
          if (Array.isArray(error.message)) return error.message.map((msg) => toast.error(msg));
          return toast.error(error.message);
        },
      }
    );

  // [NEW] Edit Mutation
  const { mutate: updateProduct, isLoading: updateProductLoading } =
    useCustomMutation(
      {
        url: `/product/edit/${initialData?._id}`, // [CHANGED] Use ID
        method: "patch",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [GET_ALL_PHARMACY_PRODUCTS] });
          toast.success("Product Updated Successfully");
          resetForm();
          closeModal();
        },
        onError: (error) => {
          if (Array.isArray(error.message)) return error.message.map((msg) => toast.error(msg));
          return toast.error(error.message);
        },
      }
    );

  // [CHANGED] Toggle logic
  const onClickSave = () => {
    let emptyRequiredFieldNumber = 0;
    requireFields.forEach((field) => {
      emptyRequiredFieldNumber = values[field] === "" ? emptyRequiredFieldNumber + 1 : emptyRequiredFieldNumber;
      setFieldTouched(field, true, true);
    });

    if (emptyRequiredFieldNumber > 0) return;

    if (isEditMode) {
      // [FIX] Sanitize payload for Update
      // Remove filename because the backend validator forbids unknown properties in JSON body
      const payload = { ...values };
      delete payload.filename;
      updateProduct(payload);
    } else {
      createProduct();
    }
  };

  const onAddImg = (file) => {
    setValues((prev) => ({ ...prev, filename: file[0] }));
  };

  return (
    <Stack spacing={2}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Drug Name"
            value={values.drugName}
            placeholder={"Enter drug name"}
            handleChange={handleChange}
            name="drugName"
            {...handleErrorProps(touched.drugName, errors.drugName)}
            onBlur={handleBlur}
          />
        </Grid>

        {/* [CHANGED] Use Autocomplete for editable dropdown/text */}
        <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
                <Typography variant="body1">Drug Type</Typography>
                <Autocomplete
                  freeSolo
                  options={drugTypes || []}
                  getOptionLabel={(option) => option.name || option}
                  // [FIX] Priority: 
                  // 1. Find in dropdown list 
                  // 2. Or use initialData populated object (so the name shows) 
                  // 3. Or fallback to the raw ID string
                  value={
                    (drugTypes || []).find(opt => opt.value === values.drugType) || 
                    getInitialOption('drugType', values.drugType) || 
                    values.drugType
                  }
                  onChange={(event, newValue) => {
                    const val = typeof newValue === 'string' ? newValue : newValue?.value;
                    setFieldValue("drugType", val);
                  }}
                  onInputChange={(event, newInputValue) => {
                     // Only update if typing manually (freeSolo)
                     if(event && event.type === 'change') setFieldValue("drugType", newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      placeholder="Select or Type Manual"
                      error={Boolean(touched.drugType && errors.drugType)}
                      helperText={touched.drugType && errors.drugType}
                      sx={{ '& .MuiOutlinedInput-root': { padding: '6px !important', backgroundColor: '#fff' } }}
                    />
                  )}
                />
            </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack spacing={0.5}>
            <Typography variant="body1">Unit</Typography>
            <Autocomplete
              freeSolo
              options={["TABLET", "VIAL", "PIECE", "CAPSULE"]}
              value={values.unit || ""}
              onChange={(e, val) => setFieldValue("unit", val || "")}
              onInputChange={(e, val) => { if(e?.type==='change') setFieldValue("unit", val || "") }}
              renderInput={(params) => <TextField {...params} sx={{ '& .MuiOutlinedInput-root': { padding: '6px !important', backgroundColor: '#fff' } }} />}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
             <Stack spacing={0.5}>
                <Typography variant="body1">Generic Name</Typography>
                <Autocomplete
                  freeSolo
                  options={genericNames || []}
                  getOptionLabel={(option) => option.name || option}
                  // [FIX] Same logic for Generic Name
                  value={
                    (genericNames || []).find(opt => opt.value === values.genericName) || 
                    getInitialOption('genericName', values.genericName) ||
                    values.genericName
                  }
                  onChange={(e, val) => setFieldValue("genericName", typeof val === 'string' ? val : val?.value)}
                  onInputChange={(e, val) => { if(e?.type==='change') setFieldValue("genericName", val) }}
                  renderInput={(params) => <TextField {...params} sx={{ '& .MuiOutlinedInput-root': { padding: '6px !important', backgroundColor: '#fff' } }} />}
                />
            </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Brand Name"
            value={values.brandName}
            name="brandName"
            placeholder={"Enter text here"}
            handleChange={handleChange}
            {...handleErrorProps(touched.brandName, errors.brandName)}
            onBlur={handleBlur}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Drug Strength"
            value={values.strength}
            name="strength"
            placeholder={"Enter text here"}
            handleChange={handleChange}
            {...handleErrorProps(touched.strength, errors.strength)}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Sales Price"
            value={values.salesPrice}
            name="salesPrice"
            type="number" // Ensures numeric input
            placeholder={"0.00"}
            handleChange={handleChange}
            {...handleErrorProps(touched.salesPrice, errors.salesPrice)}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Purchase Price"
            value={values.purchasePrice}
            name="purchasePrice"
            type="number" // Ensures numeric input
            placeholder={"0.00"}
            handleChange={handleChange}
            {...handleErrorProps(touched.purchasePrice, errors.purchasePrice)}
            onBlur={handleBlur}
          />
        </Grid>
        
        <Grid item xs={12}>
          <CustomTextInput
            title="Product Description"
            value={values.prodcutDescription}
            name="prodcutDescription"
            placeholder={"Enter text here"}
            handleChange={handleChange}
            multiline
            helperText={`${values.prodcutDescription.length}/250`}
          />
        </Grid>
      </Grid>

      {/* [CHANGED] Hide Image Upload if Edit Mode (optional choice) */}
      {!isEditMode && (
        <Stack spacing={1} width="fit-content">
            <Typography variant="heading">Add Image</Typography>
            <ImageUpload
            imgTitle=""
            imgStyle={{ borderRadius: "0%" }}
            onAddImg={onAddImg}
            blanckImg="/imgs/black-solid-icon-medicine.jpg"
            />
        </Stack>
      )}

      <Stack spacing={1} direction="row" width="fit-content">
        <CustomButton
          text={isEditMode ? "Update" : "Create"} // [CHANGED]
          color="secondary"
          onClick={onClickSave}
          disabled={createProductLoading || updateProductLoading}
        />
        <CustomButton
          text={"Cancel"}
          variant="containedBrown"
          onClick={closeModal}
        />
      </Stack>
    </Stack>
  );
}

export default CreateNewProductModal;